import BaseService from '../../base/base_service';
import ProductPackage from './product_package/product_package';
import HybridService from '../hybrid/hybrid_service';
import Serial from './serial/serial';
import Info from './info/info';
import Device from '../../utils/device/device';
import {REGULATION_CODE_TYPES} from "../regulation/regulation_const";
import ISkinService from './skin_service_interface';
import _ from '../../utils/underscore';
import {SDK_VERSION} from '../../sdk-version';

export interface ProductPackageParams {
    webApp: {
        pc: number,
        iphone: number,
        ipad: number,
        other: number,
    };
    native: {
        iphone: number,
        ipad: number,
        other: number,
    };
}

export interface SerialParams {
    other: number;
    iphoneNative: number;
    iphone: number;
    ipadNative: number;
    ipad: number;
}

export interface SkinServiceParams {
    regulationID: number;
    iosDefaultHybridSerial: string;
    androidDefaultHybridSerial: string;
    brandName: string;
    brandID: number;
    subBrandID: number;
    clientID: string;
    clientVersion: string;
    platformSystemID: string;
    minimumAge: number;
    responsibleGamblingLink: string;
    productPackage: ProductPackageParams;
    productPackageID?: number;
    serial: SerialParams;
    dateTimeFormat: string;
    dateFormat: string;
    timeFormat: string;
    dateTimeHistoryFormat: string;
    preventOpenGameForMobileWebappEnabled: boolean;
    disableCheckIfCorrectAppInUse: boolean;
    preventLoginToEnforceHybridAppForMobileWeb: boolean;
    safeToPlayURL: string;
    clientPlatform: string;
    appDownloadLinkIos: string;
    appDownloadLinkNonIos: string;
    culture: string;
    isComRelatedBrand: boolean;
    useControlCenterForReminderInterval: boolean;
    wrongLocationDetectionPath?: string;
}


/**
 *
 *
 *
 */
export default class SkinService extends BaseService implements ISkinService {
    /**
     * @static
     */
    static CLASS = 'SkinService';

    private _params: SkinServiceParams = null;
    private _platformSystemID: string = '';
    private _clientIDPattern = '';
    private _clientVersionPattern = '';
    private _productPackage: ProductPackage = null;
    private _serial: Serial = null;
    private _info: Info = null;
    private _defaultHybridSerial: string = null;
    private _clientPlatform: string = "2";

    /**
     *
     * @param params
     */
    init(params: SkinServiceParams) {
        this._params = params;
        if (params) {
            this._clientIDPattern = params.clientID;
            this._clientVersionPattern = params.clientVersion;
            this._platformSystemID = params.platformSystemID;
            this._serial = Serial.create(params.serial);
            this._clientPlatform = params.clientPlatform;

            // if (typeof params.productPackageID === 'number') {
            //     params.productPackage.productPackageID = params.productPackageID;
            // }
            this._productPackage = ProductPackage.create(params.productPackage);
            this._info = Info.create(params);
            this._setDefaultHybridSerial(params.androidDefaultHybridSerial, params.iosDefaultHybridSerial);
        }
    }


    private _setDefaultHybridSerial(androidDefaultHybridSerial: string, iosDefaultHybridSerial: string) {
        if (Device.hybrid.inCasino) {
            if (androidDefaultHybridSerial && Device.os.isAndroid) {
                this._defaultHybridSerial = androidDefaultHybridSerial;
            }
            else if (iosDefaultHybridSerial && Device.os.isIOS) {
                this._defaultHybridSerial = iosDefaultHybridSerial;
            }
        }
    }

    getDefaultHybridSerial(): string {
        return this._defaultHybridSerial;
    }

    getClientPlatform(): string {
        return this._clientPlatform;
    }

    getAppDownloadPageURL(): string {
        let link: string = "";
        if (Device.os.isIOS || Device.isProbablyIOSDevice) {
            link = this._params.appDownloadLinkIos;
        } else {
            link = this._params.appDownloadLinkNonIos;
        }

        // check if relative or absolute link
        if (!_.isAbsoluteLink(link)) {
            link = this.serviceFactory.windowService().getLocationOrigin() + link;
        }

        return link;
    }

    getSafeToPlayURL(): string {
        let absoluteLink = "";
        // check if relative or absolute link
        if (!_.isAbsoluteLink(this._params.safeToPlayURL)) {
            absoluteLink = this.serviceFactory.windowService().getLocationOrigin();
        }

        absoluteLink += this._params.safeToPlayURL;

        return absoluteLink;
    }

    shouldPreventOpenGameForMobileWebApp(): boolean {
        return this._params.preventOpenGameForMobileWebappEnabled;
    }

    disableCheckIfCorrectAppInUse(): boolean {
        return this._params.disableCheckIfCorrectAppInUse || false;
    }

    shouldPreventLoginToEnforceHybridAppForMobileWeb(): boolean {
        return Device.isMobileWebApp && this._params.preventLoginToEnforceHybridAppForMobileWeb;
    }

    getProductPackageActiveGroup(): number[] {
        return Device.isMobile() ?
            this._productPackage.getProductPackageGroupMobile()
            : this._productPackage.getProductPackageGroupPC();
    }

    getAllProductPackages(): number[] {
        const mobilePackages = this._productPackage.getProductPackageGroupMobile();
        const pcPackages = this._productPackage.getProductPackageGroupPC();

        return pcPackages.concat(mobilePackages);
    }


    getInfoData(infoOk: Function, infoError: Function) {
        return this._info.getData(infoOk, infoError);
    }


    getBrandID(): number {

        let oAuth2Service = this.serviceFactory.oAuth2Service();
        if (oAuth2Service.isAuthorized()) {
            return parseInt(oAuth2Service.getToken().brandId);
        } else {
            return this._params.brandID;
        }

    }

    getWrongLocationDetectionPath() {
        return this._params.wrongLocationDetectionPath || '';
    }

    isCOMBrand(): boolean {
        return this._params.isComRelatedBrand;
    }

    getSubBrandID(): number {
        let oAuth2Service = this.serviceFactory.oAuth2Service();
        if (oAuth2Service.isAuthorized()) {
            return parseInt(oAuth2Service.getToken().subBrandId);
        } else {
            return this._params.subBrandID;
        }
    }

    getRegulationID(): number {
        let oAuth2Service = this.serviceFactory.oAuth2Service();
        if (oAuth2Service.isAuthorized()) {
            return parseInt(oAuth2Service.getToken().regId);
        } else {
            if (this.serviceFactory.countryService().isUK()) {
                return REGULATION_CODE_TYPES.UK;
            }
            return this._params.regulationID;
        }
    }


    /**
     * Get the clientID
     *
     */
    getClientID(): string {
        return this._clientIDPattern
            .replace('{{BRANDNAME}}', this.serviceFactory.windowService().getDomain())
            .replace('{{BRANDID}}', this.getBrandID().toString())
            .replace('{{LANG}}', this.serviceFactory.languageService().getISO3())
            .replace('{{PRODUCTPACKAGEID}}', this.getProductPackageID().toString())
            .replace('{{SUBRANDID}}', this.getSubBrandID().toString())
            .toUpperCase();
    }

    private _getOrbitVersion() {
        const orbit = this.serviceFactory.windowService().getGlobalProperty('orbit');
        if(orbit) {
            return `,${orbit.version}`;
        } else {
            return '';
        }
    }

    private _getIsPWA(): string {
        if (Device.isProgressiveWebApp) {
            return "-PWA";
        }
        return "";
    }

    getPlatformSystemID(): string {
        return this._platformSystemID;
    }

    /**
     * Get client version
     *
     */
    getVersion(): string {
        const outputVersion = this._clientVersionPattern
            .replace('{{BRANDNAME}}', this.serviceFactory.windowService().getDomain())
            .replace('{{BRANDID}}', this.getBrandID().toString())
            .replace('{{LANG}}', this.serviceFactory.languageService().getISO3())
            .replace('{{PRODUCTPACKAGEID}}', this.getProductPackageID().toString())
            .replace('{{SUBRANDID}}', this.getSubBrandID().toString())
            .replace('{{CGP_VERSION}}', SDK_VERSION)
            .replace('{{HYBRID_VERSION}}', HybridService.create().getVersion())
            .replace('{{ORBIT_VERSION}}', this._getOrbitVersion())
            .replace('{{PWA}}', this._getIsPWA())
            .toUpperCase();

        return outputVersion;
    }


    /**
     * Getter for the minimum age
     */
    getMinimumAge(): number {
        return this._params.minimumAge ? this._params.minimumAge : 18;
    }


    /**
     * Getter for the responsible gambling link
     */
    getResponsibleGamblingLink(): string {
        const url = this._params.responsibleGamblingLink;
        if (_.isAbsolutUrl(url)) {
            return url;
        } else {
            let responsibleUrl = new URL(this.serviceFactory.windowService().getLocationOrigin());
            responsibleUrl.pathname = url;
            return responsibleUrl.href;
        }
    }


    /**
     * Getter brand name.
     *
     */
    getBrandName(): string {
        return this._params.brandName;
    }

    /**
     * When true, take the reminder interval from control center, else take it from localstorage (old implementation)
     * For UK
     */
    useControlCenterForReminderInterval(): boolean {
        return this._params.useControlCenterForReminderInterval;
    }

    /**
     * Getter for the active product package ID.
     *
     */
    getProductPackageID(): number {
        return this._productPackage.getProductPackageID();
    }


    /**
     * Getter serial.
     */
    getSerialData(marketingData, GNWPRequestModel) {
        return this._serial.getSerialData(marketingData, GNWPRequestModel);
    }


    /**
     * Getter dateTime format
     *
     */
    getDateTimeFormat(): string {
        return this._params.dateTimeFormat;
    }


    /**
     * Getter dateTime format
     *
     */
    getDateFormat(): string {
        return this._params.dateFormat;
    }


    /**
     * Getter dateTime format
     *
     */
    getTimeFormat(): string {
        return this._params.timeFormat;
    }


    /**
     * Getter dateTime format
     *
     */
    getDateTimeHistoryFormat(): string {
        return this._params.dateTimeHistoryFormat;
    }

    getCulture(): string {
        return this._params.culture;
    }
}
